/**
 * Vaste carrousel: gelijke image-hoogte per rij via --ratio (geen wachten op img decode).
 * Zet --carousel-row-image-lock + class op slideshow-slides; lock groeit alleen monotoon.
 */
const SCROLLER_SELECTOR =
  'slideshow-component.carousel-column-mode-fixed.carousel-content slideshow-slides';

/** @param {Element} scroller @returns {HTMLElement[]} */
function getImageBlocks(scroller) {
  return Array.from(
    scroller.querySelectorAll(':scope > .card .card__inner .image-block.spacing-style')
  );
}

/** @param {Element} scroller @returns {number} */
function getInlineWidth(scroller) {
  return Math.round(scroller.getBoundingClientRect().width);
}

/** @param {Element} scroller */
function unlock(scroller) {
  scroller.classList.remove('carousel-fixed-row-images--locked');
  scroller.style.removeProperty('--carousel-row-image-lock');
}

/**
 * CSS aspect-ratio: width / height → height = width * (h / w).
 * @param {string} ratioRaw
 * @returns {number | null}
 */
function parseRatioHeightMultiplier(ratioRaw) {
  const raw = ratioRaw.trim();
  if (!raw || raw === 'auto') return null;

  const parts = raw.split('/').map((part) => parseFloat(part.trim()));
  if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
    return parts[1] / parts[0];
  }

  const single = parseFloat(raw);
  if (single > 0) {
    return 1 / single;
  }

  return null;
}

/**
 * @param {HTMLElement} block
 * @returns {number | null}
 */
function expectedHeightFromRatio(block) {
  const ratioRaw = getComputedStyle(block).getPropertyValue('--ratio');
  const multiplier = parseRatioHeightMultiplier(ratioRaw);
  if (multiplier == null) return null;

  const width = block.getBoundingClientRect().width;
  if (width <= 0) return null;

  return Math.ceil(width * multiplier);
}

/** @param {HTMLElement} block */
function needsMeasuredHeight(block) {
  return expectedHeightFromRatio(block) == null;
}

/**
 * @param {Element} scroller
 */
function lockHeights(scroller) {
  const blocks = getImageBlocks(scroller);
  if (blocks.length < 2) {
    unlock(scroller);
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const heights = blocks.map((el) => {
        const fromRatio = expectedHeightFromRatio(el);
        if (fromRatio != null) return fromRatio;
        return Math.ceil(el.getBoundingClientRect().height);
      });

      let maxH = Math.ceil(Math.max(0, ...heights));
      if (maxH <= 0) return;

      const currentLock =
        parseFloat(scroller.style.getPropertyValue('--carousel-row-image-lock')) ||
        parseFloat(getComputedStyle(scroller).getPropertyValue('--carousel-row-image-lock')) ||
        0;
      maxH = Math.max(currentLock, maxH);

      scroller.style.setProperty('--carousel-row-image-lock', `${maxH}px`);
      scroller.classList.add('carousel-fixed-row-images--locked');
    });
  });
}

/** @param {() => void} fn @param {number} ms */
function debounce(fn, ms) {
  let t = 0;
  return () => {
    clearTimeout(t);
    t = setTimeout(fn, ms);
  };
}

/** @param {Element} scroller */
function cleanupScroller(scroller) {
  if (scroller._carouselImageLockAbort) {
    scroller._carouselImageLockAbort.abort();
    scroller._carouselImageLockAbort = null;
  }
  if (scroller._carouselImageLockResizeObserver) {
    scroller._carouselImageLockResizeObserver.disconnect();
    scroller._carouselImageLockResizeObserver = null;
  }
  unlock(scroller);
  delete scroller.dataset.carouselFixedRowImages;
  delete scroller._carouselImageLockInlineWidth;
}

/** @param {Element} scroller */
function setupScroller(scroller) {
  if (!(scroller instanceof Element) || scroller.dataset.carouselFixedRowImages === 'true') {
    return;
  }
  scroller.dataset.carouselFixedRowImages = 'true';

  const ac = new AbortController();
  const { signal } = ac;
  const schedule = debounce(() => lockHeights(scroller), 200);
  const scheduleForInlineResize = debounce(() => {
    const inlineWidth = getInlineWidth(scroller);
    if (inlineWidth === 0 || inlineWidth === scroller._carouselImageLockInlineWidth) return;

    scroller._carouselImageLockInlineWidth = inlineWidth;
    lockHeights(scroller);
  }, 200);

  scroller._carouselImageLockInlineWidth = getInlineWidth(scroller);

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(scheduleForInlineResize);
    resizeObserver.observe(scroller);
    scroller._carouselImageLockResizeObserver = resizeObserver;
  } else {
    window.addEventListener('resize', scheduleForInlineResize, { signal, passive: true });
  }

  window.addEventListener('orientationchange', schedule, { signal });

  for (const block of getImageBlocks(scroller)) {
    if (!needsMeasuredHeight(block)) continue;

    const img = block.querySelector('img.image-block__image');
    if (img) {
      img.addEventListener('load', schedule, { signal });
    }
  }

  scroller._carouselImageLockAbort = ac;

  lockHeights(scroller);
}

function init() {
  for (const el of document.querySelectorAll(SCROLLER_SELECTOR)) {
    setupScroller(el);
  }
}

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target;
  if (!(root instanceof Element)) return;
  for (const el of root.querySelectorAll(SCROLLER_SELECTOR)) {
    cleanupScroller(el);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
